// EL_ApproveDeliveryDockets.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import images
import goNormal from '../Images/go_n.gif';
import goOver from '../Images/go_o.gif';
import goDown from '../Images/go_d.gif';

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

// Custom Confirmation Dialog Component
const ConfirmationDialog: React.FC<{
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isOpen: boolean;
}> = ({ message, onConfirm, onCancel, isOpen }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        background: "white",
        padding: "20px",
        borderRadius: "5px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        maxWidth: "400px",
        width: "90%"
      }}>
        <h3 style={{ marginTop: 0 }}>Confirm</h3>
        <p>{message}</p>
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <button
            onClick={onCancel}
            style={{
              marginRight: "10px",
              padding: "5px 15px",
              backgroundColor: "#f0f0f0",
              border: "1px solid #ccc",
              borderRadius: "3px",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "5px 15px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer"
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

interface Docket {
  DistributorName: string;
  OrderDate: string;
  OrderRef: string;
  DateOfSale: string;
  Product: string;
  Family: {
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    Name: string;
  };
  Paid: string;
  UpFront: string;
  DDCode: string;
  TotalAmount: number;
  IsFinanceSale: boolean;
  ApprovalStatus: string;
}

interface School {
  SchoolCode: string;
  Name: string;
}

const EL_ApproveDeliveryDockets: React.FC = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [dockets, setDockets] = useState<Docket[]>([]);
  const [creditLimit, setCreditLimit] = useState(0);
  const [creditRemaining, setCreditRemaining] = useState(0);
  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [isHO, setIsHO] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});

  // Get session data from your auth context or sessionStorage
  const country = sessionStorage.getItem('SES_Country') || '';
  const supportSchoolCode = sessionStorage.getItem('SES_SupportSchoolCode') || '';
  const category = sessionStorage.getItem('SES_Category') || '';

  document.body.style.backgroundColor = "azure";

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/get-schools`);
      const data = await response.json();

      if (data.error) {
        setErrorMessage(data.error);
        return;
      }

      setSchools(data.schools);
      setIsHO(data.is_ho);
      setShowCreditDetails(data.show_credit_details);
      setCreditLimit(data.credit_limit);
      setCreditRemaining(data.credit_remaining);

      // Set default school if not HO
      if (!data.is_ho && data.schools.length > 0) {
        setSelectedSchool(data.schools[0][0]); // First school's code
        fetchDockets(data.schools[0][0], 1, 20);
      }
    } catch (error) {
      setErrorMessage('Error loading schools. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDockets = async (schoolCode: string, page: number, rowsPerPage: number) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/api/get-delivery-dockets?schoolCode=${schoolCode}&page=${page}&rowsPerPage=${rowsPerPage}`
      );
      const data = await response.json();

      if (data.error) {
        setErrorMessage(data.error);
        return;
      }

      setDockets(data.data);
      setTotalCount(data.total_count);
      setPage(data.page);
      setRowsPerPage(data.rows_per_page);
    } catch (error) {
      setErrorMessage('Error loading dockets. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSchool(e.target.value);
    fetchDockets(e.target.value, 1, rowsPerPage);
  };

  const handleGoClick = () => {
    if (selectedSchool) {
      fetchDockets(selectedSchool, 1, rowsPerPage);
    }
  };

  const showConfirmation = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmDialog(true);
  };

  const handleApprove = (ddCode: string, schoolCode: string, familyCode: string, login: string, price: number) => {
    if (creditRemaining <= 0) {
      alert(`Please set credit limit for ${schools.find(s => s.SchoolCode === schoolCode)?.Name || schoolCode}.`);
      return;
    }

    if (price > creditRemaining) {
      showConfirmation(
        `The price exceeds the credit limit. Do you want to override and approve the sale?`,
        () => {
          // Call approve API
          fetch(`${API_BASE}/api/approve-docket`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              schoolCode,
              familyCode,
              login,
              ddCode
            })
          })
          .then(response => response.json())
          .then(data => {
            if (data.success) {
              fetchDockets(selectedSchool, page, rowsPerPage);
            } else {
              setErrorMessage(data.error || 'Error approving docket');
            }
          })
          .catch(error => {
            setErrorMessage('Error approving docket. Please try again.');
            console.error(error);
          });
        }
      );
      return;
    }

    // Call approve API directly if no confirmation needed
    fetch(`${API_BASE}/api/approve-docket`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        schoolCode,
        familyCode,
        login,
        ddCode: ddCode
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        fetchDockets(selectedSchool, page, rowsPerPage);
      } else {
        setErrorMessage(data.error || 'Error approving docket');
      }
    })
    .catch(error => {
      setErrorMessage('Error approving docket. Please try again.');
      console.error(error);
    });
  };

  const handleConfirm = () => {
    confirmAction();
    setShowConfirmDialog(false);
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  const handleModifyCreditLimit = (e: React.MouseEvent) => {
    e.preventDefault();
    const school = schools.find(s => s.SchoolCode === selectedSchool);
    if (school) {
      const url = `/EL_ModifyCreditLimit?SchoolCode=${selectedSchool}&SchoolName=${encodeURIComponent(school.Name)}&CreditLimit=${creditLimit}`;
      navigate(url);
    }
  };

  const handleUploadSaleDocuments = (schoolCode: string, familyCode: string, login: string) => {
    const url = `/SALES/EL_PreAudit?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${login}`;
    navigate(url);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchDockets(selectedSchool, newPage, rowsPerPage);
  };

  const handleFamilyClick = (schoolCode: string, familyCode: string, login: string) => {
    navigate(`/EL_SalesModify?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${login}&Previous=/DEALER/EL_ApproveDeliveryDockets`);
  };

  return (
    <div style={{ backgroundColor: "azure", margin: 0, padding: 10 }}>
      {/* Header */}
      <h3 style={{textAlign:"center", fontFamily:"Arial", marginTop:"0", marginBottom:"5px", color:"navy"}}>Kinetic Education</h3>
      <div style={{ display: "flex", justifyContent: "flex-end", gap:"20px", fontFamily:"Arial", fontSize:"10pt" }}>
      <a href="/DEALER/EL_Menu" style={{ textDecoration: "underline", color: "blue", fontWeight:"bold" }}>Back To Menu</a>
      <a href="/DEALER/EL_Logout" style={{ textDecoration: "underline", color: "blue", fontWeight:"bold" }}>Logout</a>
      </div>
      <table
        id="tblHeader"
        cellPadding="5"
        cellSpacing="0"
        style={{
          width: "100%",
          fontFamily: "Arial",
          fontSize: "12pt",
          color: "Red"
        }}
      >
        <tbody>
          <tr>
            <td align="center">
              <b>Pre-Audit Sales</b>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Filters */}
      <table
        cellPadding="2"
        cellSpacing="0"
        style={{
          width: "100%",
          border: showCreditDetails ? "1px solid red" : "none"
        }}
      >
        <tbody>
          <tr id="trFilters" style={{ display: category !== "DA" ? "table-row" : "none" }}>
            <td align="left" style={{ paddingLeft: "5px", paddingBottom: "10px", width: "50%", verticalAlign: "bottom", border: "1px solid red" }}>
              Distributor:&nbsp;
              <select
                id="ddlSchools"
                value={selectedSchool}
                onChange={handleSchoolChange}
                disabled={isHO}
                style={{
                  fontFamily: "Trebuchet MS",
                  fontSize: "8pt",
                  border: "solid 1px gray",
                  backgroundColor: "white"
                }}
              >
                {schools.map(school => (
                  <option key={school.SchoolCode} value={school.SchoolCode}>
                    {school.Name}
                  </option>
                ))}
              </select>
              &nbsp;&nbsp;&nbsp;
              {!isHO && (
                <img
                  src={goNormal}
                  alt="Go"
                  onClick={handleGoClick}
                  onMouseOver={(e) => (e.currentTarget as HTMLImageElement).src = goOver}
                  onMouseOut={(e) => (e.currentTarget as HTMLImageElement).src = goNormal}
                  onMouseDown={(e) => (e.currentTarget as HTMLImageElement).src = goDown}
                  style={{ cursor: "pointer", verticalAlign: "middle" }}
                />
              )}
            </td>
            <td style={{ border: "1px solid red", width: "50%", paddingRight: "5px", textAlign: "right" }}>
              {showCreditDetails && (
                <>
                  <span>
                    <b>
                      <a href="#" onClick={handleModifyCreditLimit} style={{ textDecoration: "none" }}>
                        Credit Limit:
                      </a>
                    </b>
                    &nbsp;$&nbsp;{creditLimit.toFixed(2)}
                    &nbsp;&nbsp;&nbsp;
                    <b>Credit Remaining:</b>
                    &nbsp;$&nbsp;
                    <span style={{ color: creditRemaining < 0 ? "red" : "black" }}>
                      {creditRemaining.toFixed(2)}
                    </span>
                  </span>
                </>
              )}
            </td>
          </tr>

          {/* Dockets Table */}
          <tr>
            <td colSpan={2} align="left" style={{ paddingLeft: "5px" }}>
              <div style={{
                width: "100%",
                overflowX: "auto",
                marginBottom: "20px"
              }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "Arial",
                  fontSize: "11pt"
                }}>
                  <thead>
                    <tr style={{ backgroundColor: "#c7ddfd", color:"#2859ac", fontWeight:"normal"}}>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Distributor</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Order Date</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Order Reference</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Sale Date</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Product</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Family</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Amount Paid</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>UPFront</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Approve Sale</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Document Approval Status</th>
                      <th style={{ padding: "5px", border: "1px solid #ddd", textAlign: "left" }}>Upload Sale Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dockets.map((docket, index) => (
                      <tr key={index}>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.DistributorName}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.OrderDate}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.OrderRef}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.DateOfSale}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.Product}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>
                          <a
                            href="#"
                            onClick={() => handleFamilyClick(docket.Family.SchoolCode, docket.Family.FamilyCode, docket.Family.Login)}
                            style={{ textDecoration: "none", color: "#0066cc" }}
                          >
                            {docket.Family.Name}
                          </a>
                        </td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.Paid}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.UpFront}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>
                          <button
                            onClick={() => handleApprove(
                              docket.DDCode,
                              docket.Family.SchoolCode,
                              docket.Family.FamilyCode,
                              docket.Family.Login,
                              docket.TotalAmount
                            )}
                            disabled={docket.ApprovalStatus === "Approved"}
                            style={{
                              padding: "3px 8px",
                              backgroundColor: docket.ApprovalStatus === "Approved" ? "#cccccc" : "#4CAF50",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              cursor: docket.ApprovalStatus === "Approved" ? "not-allowed" : "pointer"
                            }}
                          >
                            Approve
                          </button>
                          {docket.IsFinanceSale && <span style={{ color: "red", marginLeft: "5px" }}>(F)</span>}
                        </td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>{docket.ApprovalStatus}</td>
                        <td style={{ padding: "5px", border: "1px solid #ddd" }}>
                          <button
                            onClick={() => handleUploadSaleDocuments(
                              docket.Family.SchoolCode,
                              docket.Family.FamilyCode,
                              docket.Family.Login
                            )}
                            style={{
                              padding: "3px 8px",
                              backgroundColor: "#2196F3",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              cursor: "pointer",
                              width: "150px"
                            }}
                          >
                            Pre-Audit Sale Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalCount > rowsPerPage && (
                <div style={{ textAlign: "center", marginTop: "10px" }}>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    style={{
                      padding: "5px 10px",
                      margin: "0 5px",
                      backgroundColor: page === 1 ? "#cccccc" : "#f0f0f0",
                      cursor: page === 1 ? "not-allowed" : "pointer"
                    }}
                  >
                    Previous
                  </button>
                  <span>Page {page} of {Math.ceil(totalCount / rowsPerPage)}</span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= Math.ceil(totalCount / rowsPerPage)}
                    style={{
                      padding: "5px 10px",
                      margin: "0 5px",
                      backgroundColor: page >= Math.ceil(totalCount / rowsPerPage) ? "#cccccc" : "#f0f0f0",
                      cursor: page >= Math.ceil(totalCount / rowsPerPage) ? "not-allowed" : "pointer"
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />
    </div>
  );
};

export default EL_ApproveDeliveryDockets;